import { InputDialog } from "@/components/customComponents/InputDialog";
import HeaderCommon from "@/components/header/HeaderCommon";
import TabBar from "@/components/tabBar/TabBar";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import userProfileApi, {
  UserProfile as UserProfileType,
  TenantRelationship,
  NotificationType,
  EnableNotification,
} from "@/api-client/api/services/userProfile/userProfile";
import Edit from "../assets/icon/svgs/Edit.svg?react";
import Mail from "../assets/icon/svgs/Mail.svg?react";
import Call from "../assets/icon/svgs/Call.svg?react";
import LogOut from "../assets/icon/svgs/LogOut.svg?react";
import TrashBind from "../assets/icon/svgs/TrashBind.svg?react";
import TelegramIcon from "../assets/icon/svgs/Telegram.svg?react";
import WhatsAppIcon from "../assets/icon/svgs/Whatsapp.svg?react";
import Plus from "../assets/icon/svgs/Plus.svg?react";
import { LOGIN_ROUTE } from "@/lib/constants/routes";
import { useUserContext } from "@/contexts/UserContextProvider";
import { useNavigate } from "react-router-dom";
import { logout } from "@/utils/loginHelper";
import { PopUpDeleteAccount } from "@/components/pop-ups/PopUpDeleteAccount";
import { Switch } from "@/components/ui/switch";
import { PopUpAddRenter } from "@/components/pop-ups/PopUpAddRenter";
import { PopUpEditRenter } from "@/components/pop-ups/PopUpEditRenter";
import { PopUpLinkTelegram } from "@/components/pop-ups/PopUpLinkTelegram";
import { PopUpVerifiNumber } from "@/components/pop-ups/PopUpVerifiNumber";
import { Button } from "@/components/ui/button";
import { ConfirmUnbindLink } from "@/components/pop-ups/ConfirmUnbindLink";

interface FormData {
  first_name: string;
  last_name: string;
  phone_number: string;
  telegram: string;
  whatsapp: string;
  info: string;
}

type ChanelName =
  | "enable_sms"
  | "enable_email"
  | "enable_whatsapp"
  | "enable_telegram"
  | "invoice_created"
  | "invoice_to_debt"
  | "house_notice_created";

const UserProfile = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { refreshUser } = useUserContext();

  const [isPopUpOpenTelegramLink, setIsPopUpOpenTelegramLink] = useState(false);
  const [telegramLink, setTelegramLink] = useState<string | undefined>();
  const [enableNotification, setEnableNotification] =
    useState<EnableNotification>();
  const [channelName, setChannelName] = useState<
    "enable_whatsapp" | "enable_sms" | "enable_telegram" | undefined
  >();
  const [isPopUpOpenVerifiNumber, setIsPopUpOpenVerifiNumber] = useState(false);
  const [isEditingUserProfile, setIsEditingUserProfile] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfileType | null>(null);
  const [tenantRelationships, setTenantRelationships] =
    useState<TenantRelationship | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [unboundErrors, setUnboundErrors] = useState<{
    [key in
      | "enable_sms"
      | "enable_telegram"
      | "enable_whatsapp"
      | "enable_email"]?: string;
  }>({});

  const [statusOfNotifications, setStatusOfNotifications] = useState([
    { type: "enable_sms", enabled: false },
    { type: "enable_email", enabled: false },
    { type: "enable_whatsapp", enabled: false },
    { type: "enable_telegram", enabled: false },
    { type: "invoice_created", enabled: false },
    { type: "invoice_to_debt", enabled: false },
    { type: "house_notice_created", enabled: false },
  ]);

  const methods = useForm<FormData>({
    defaultValues: {
      first_name: userProfile?.first_name || "",
      last_name: userProfile?.last_name || "",
      // phone_number: userProfile?.phone_number || "",
      // telegram: userProfile?.telegram || "",
      // whatsapp: userProfile?.whatsapp || "",
      info: userProfile?.info || "",
    },
  });

  const {
    setValue,
    getValues,
    watch,
    // setError,
    clearErrors,
    formState: { errors },
  } = methods;

  const onOffNotification = (type: ChanelName, value: boolean) => {
    setStatusOfNotifications((prev) =>
      prev.map((item) =>
        item.type === type ? { ...item, enabled: value } : item
      )
    );
  };

  const isEnabled = (name: ChanelName) =>
    statusOfNotifications.find((item) => item.type === name)?.enabled ?? false;

  const handleChangeHouseAdminDetails = (
    key: keyof FormData,
    value: string
  ) => {
    setValue(key, value);
    if (value) {
      clearErrors(key);
    }
  };

  const handleUnlinkChannel = async (
    channel: "sms" | "telegram" | "whatsapp"
  ) => {
    const channelMapping: Record<
      string,
      "enable_sms" | "enable_whatsapp" | "enable_telegram"
    > = {
      sms: "enable_sms",
      telegram: "enable_whatsapp",
      whatsapp: "enable_telegram",
    };
    const apiChannelName = channelMapping[channel];

    try {
      await userProfileApi.postDisableTelegraNotifications({ channel });
      onOffNotification(apiChannelName, false);
      getUserProfile();
    } catch (error) {
      console.error("Error while unlinking channel:", error);
    }
  };

  const getUserProfile = async () => {
    try {
      setIsLoading(true);
      const response = await userProfileApi.getUserProfile();
      setUserProfile(response);
    } catch (error) {
      console.error("Error while fetching user profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const pollUserProfileUntilTelegram = async (
    interval = 3000,
    maxAttempts = 10
  ) => {
    let attempts = 0;

    const intervalId = setInterval(async () => {
      try {
        const profile = await userProfileApi.getUserProfile();
        if (profile.telegram) {
          setUserProfile(profile);
          onOffNotification("enable_telegram", true);
          clearInterval(intervalId);
          console.log("Telegram linked, polling stopped.");
        }
      } catch (error) {
        console.error("Polling error:", error);
      }

      attempts++;
      if (attempts >= maxAttempts) {
        clearInterval(intervalId);
        console.warn("Polling stopped after max attempts");
      }
    }, interval);
  };

  const getNotificationsPreferences = async () => {
    try {
      setIsLoading(true);
      const response = await userProfileApi.getNotificationsPreferences();
      setEnableNotification(response);
    } catch (error) {
      console.error("Error while fetching user profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTenantRelationships = async () => {
    const response = await userProfileApi.getTenantRelationships();
    setTenantRelationships(response);
  };

  const sendEditedUserProfile = async () => {
    const formData = getValues();

    try {
      await userProfileApi.patchUserProfile(formData);
      getUserProfile();
    } catch (error) {
      console.error("Error while sending user profile:", error);
    }
  };

  const subscribeTelegramNotifi = async () => {
    try {
      const telegramLink =
        await userProfileApi.postEnableTelegraNotifications();

      if (telegramLink.telegram_link) {
        setTelegramLink(telegramLink.telegram_link);
        setIsPopUpOpenTelegramLink(true);
      } else if (telegramLink.details) {
        onOffNotification("enable_telegram", true);
      }
    } catch (error) {
      console.error("notifi telegram linking", error);
    }
  };

  const linkNotification = async ({
    channelName,
  }: {
    channelName: "SMS" | "Whatsapp" | "Telegram";
    val?: boolean;
  }) => {
    const channelMapping: Record<
      string,
      "enable_sms" | "enable_whatsapp" | "enable_telegram"
    > = {
      SMS: "enable_sms",
      Whatsapp: "enable_whatsapp",
      Telegram: "enable_telegram",
    };

    const apiChannelName = channelMapping[channelName];

    try {
      const subscribeWhatsappNotifi =
        await userProfileApi.postSwitchingNotificatisPreferencesChannel({
          channel: apiChannelName,
          enable: !isEnabled(apiChannelName),
        });
      if (
        subscribeWhatsappNotifi.message == "unbound" &&
        (apiChannelName == "enable_sms" || apiChannelName == "enable_whatsapp")
      ) {
        setChannelName(apiChannelName);
        setIsPopUpOpenVerifiNumber(true);
      } else if (
        subscribeWhatsappNotifi.message == "unbound" &&
        apiChannelName == "enable_telegram"
      ) {
        subscribeTelegramNotifi();
      } else {
        return;
      }
      return subscribeWhatsappNotifi;
    } catch (error) {
      console.error("notifi email linking", error);
    }
  };

  const switchingNotificatisPreferencesChannel = async ({
    channelName,
    val,
  }: {
    channelName:
      | "enable_sms"
      | "enable_telegram"
      | "enable_whatsapp"
      | "enable_email";
    val?: boolean;
  }) => {
    const channelMapping: Record<
      string,
      "SMS" | "Whatsapp" | "Telegram" | "Email"
    > = {
      enable_email: "Email",
      enable_sms: "SMS",
      enable_whatsapp: "Whatsapp",
      enable_telegram: "Telegram",
    };

    const apiChannelName = channelMapping[channelName];

    try {
      const NotificatisPreferencesChannel =
        await userProfileApi.postSwitchingNotificatisPreferencesChannel({
          channel: channelName,
          enable: !isEnabled(channelName),
        });

      if (NotificatisPreferencesChannel.message === "unbound") {
        setUnboundErrors((prev) => ({
          ...prev,
          [channelName]: t("bind_button") + " " + `${apiChannelName}`,
        }));
        return;
      } else {
        setUnboundErrors((prev) => ({
          ...prev,
          [channelName]: null,
        }));
        onOffNotification(channelName, val ?? false);
      }
      return NotificatisPreferencesChannel;
    } catch (error) {
      console.error("notifi email linking", error);
    }
  };

  const switchingNotificatisPreferencesEvent = async ({
    eventName,
  }: {
    eventName: Array<
      "invoice_created" | "invoice_to_debt" | "house_notice_created"
    >;
  }) => {
    try {
      const unSubscribeTelegramL =
        await userProfileApi.postSwitchingNotificatisPreferencesEvent({
          events: eventName,
          enable: !isEnabled(eventName[0]),
        });
      return unSubscribeTelegramL;
    } catch (error) {
      console.error("notifi email linking", error);
    }
  };

  const handleEdit = () => {
    if (isEditingUserProfile) {
      sendEditedUserProfile();
    }

    setIsEditingUserProfile((prev) => !prev);
  };

  const handleValueChange = async (value: string) => {
    if (value === "logout") {
      await logout(refreshUser);
      navigate(LOGIN_ROUTE);
    }
  };

  const parseTelegramLink = (link: string) => {
    const [, telegramId] = link.split(":");
    return telegramId
      ? `${t("user_profile.telegram_chat_id")} ${telegramId}`
      : link;
  };

  useEffect(() => {
    if (enableNotification) {
      const notificationSettings: NotificationType[] = [
        "enable_sms",
        "enable_email",
        "enable_whatsapp",
        "enable_telegram",
        "invoice_created",
        "invoice_to_debt",
        "house_notice_created",
      ];

      const updatedStatuses = notificationSettings.map((type) => ({
        type,
        enabled: enableNotification[type] ?? false,
      }));

      setStatusOfNotifications(updatedStatuses);
    }
  }, [enableNotification]);

  useEffect(() => {
    fetchTenantRelationships();
    getNotificationsPreferences();
  }, []);

  useEffect(() => {
    getUserProfile();
  }, []);

  useEffect(() => {
    methods.reset({
      first_name: userProfile?.first_name || "",
      last_name: userProfile?.last_name || "",
      phone_number: userProfile?.phone_number || "",
      telegram: userProfile?.telegram || "",
      whatsapp: userProfile?.whatsapp || "",
      info: userProfile?.info || "",
    });
  }, [userProfile]);

  const UnbindComponent = ({
    type,
    data,
  }: {
    type: "Whatsapp" | "Telegram" | "SMS";
    data: string | undefined;
  }) => {
    return (
      <div className="flex flex-row justify-between items-center gap-4 p-3 border-1 rounded-[16px] border-[1px] border-[#0000001A]">
        <div className="flex flex-col font-medium gap-1">
          <span className="text-[12px] text-[#00000080]">{type}</span>
          <span
            className={`text-[18px] leading-[110%] ${
              data ? "text-[#000000]" : "text-[#00000080]"
            }`}
          >
            {data ? data : t("user_profile.unattached")}
          </span>
        </div>
        <div>
          {data ? (
            <ConfirmUnbindLink
              type={type}
              trigger={
                <Button
                  variant={`${data ? "link" : "system"}`}
                  className={`text-[20px] px-[30px] ${
                    data ? "text-[#0064FF] px-0" : "text-white"
                  }`}
                  onClick={() => {}}
                >
                  {data ? t("unbind_button") : t("bind_button")}
                </Button>
              }
              onConfirm={() => {
                handleUnlinkChannel(
                  type.toLowerCase() as "sms" | "telegram" | "whatsapp"
                );
              }}
            />
          ) : (
            <Button
              variant={`${data ? "link" : "system"}`}
              className={`text-[20px] px-[30px] ${
                data ? "text-[#0064FF] px-0" : "text-white"
              }`}
              onClick={() => {
                linkNotification({
                  channelName: type,
                });
              }}
            >
              {data ? t("unbind_button") : t("bind_button")}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="h-full flex flex-row p-4 gap-6 flex-1">
        <PopUpLinkTelegram
          openPopUp={isPopUpOpenTelegramLink}
          telegramLink={telegramLink}
          onClose={() => setIsPopUpOpenTelegramLink(false)}
          setTelegramToggle={() => pollUserProfileUntilTelegram()}
        />
        <PopUpVerifiNumber
          openPopUp={isPopUpOpenVerifiNumber}
          fetchUserProfile={() => getUserProfile()}
          onClose={() => setIsPopUpOpenVerifiNumber(false)}
          chanelType={channelName}
        />
        <TabBar />
        <div className="w-0 flex flex-col gap-5 flex-1">
          <div className="flex justify-between items-center">
            <p className="font-medium text-[20px]">
              {t("sideBar.profile_butt")}
            </p>
            <HeaderCommon />
          </div>
          <div className="flex flex-row gap-8">
            <div className="flex flex-1 flex-col gap-10">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-5">
                  <div className="flex flex-row justify-between">
                    <div className="flex flex-row items-end gap-4">
                      <span className="text-[24px] font-medium">
                        {t("user_profile.your_data")}
                      </span>
                      <span className="text-[12px] text-[#00000080]">
                        {t("user_profile.your_data_description")}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-row gap-8">
                    {isEditingUserProfile ? (
                      <div className="flex-1 flex flex-col gap-6">
                        <form onSubmit={(e) => e.preventDefault()}>
                          <div className="flex-1 flex flex-col gap-4">
                            <div className="grid grid-cols-2 gap-x-[10px] gap-y-[16px]">
                              <InputDialog
                                id="userProfile.first_name"
                                validationError={
                                  errors.first_name?.message || ""
                                }
                                placeholder={t("house_manager.first_name")}
                                isBiggerInput
                                inputValue={watch("first_name")}
                                disablePlaceholderAnimation={false}
                                setValidationError={() =>
                                  clearErrors("first_name")
                                }
                                setInputValue={(value) =>
                                  handleChangeHouseAdminDetails(
                                    "first_name",
                                    value
                                  )
                                }
                              />

                              <InputDialog
                                id="userProfile.last_name"
                                validationError={
                                  errors.last_name?.message || ""
                                }
                                placeholder={t("house_manager.last_name")}
                                isBiggerInput
                                inputValue={watch("last_name")}
                                disablePlaceholderAnimation={false}
                                setValidationError={() =>
                                  clearErrors("last_name")
                                }
                                setInputValue={(value) =>
                                  handleChangeHouseAdminDetails(
                                    "last_name",
                                    value
                                  )
                                }
                              />
                              {/* <InputDialog
                            id="userProfile.phone_number"
                            readOnly={true}
                            validationError={errors.phone_number?.message || ""}
                            placeholder={t("house_manager.phone_number")}
                            isBiggerInput
                            inputValue={watch("phone_number")}
                            disablePlaceholderAnimation={false}
                            setValidationError={() =>
                              clearErrors("phone_number")
                            }
                            setInputValue={(value) => {
                              const filtered = value.replace(/[^\d+\s]/g, "");
                              handleChangeHouseAdminDetails(
                                "phone_number",
                                filtered
                              );
                            }}
                          />
                          <InputDialog
                            id="userProfile.telegram"
                            readOnly={true}
                            validationError={errors.telegram?.message || ""}
                            placeholder="Telegram"
                            isBiggerInput
                            inputValue={watch("telegram")}
                            disablePlaceholderAnimation={false}
                            setValidationError={() => clearErrors("telegram")}
                            setInputValue={(value) =>
                              handleChangeHouseAdminDetails("telegram", value)
                            }
                          />
                          <InputDialog
                            id="userProfile.whatsapp"
                            readOnly={true}
                            validationError={errors.whatsapp?.message || ""}
                            placeholder="Whatsapp"
                            isBiggerInput
                            inputValue={watch("whatsapp")}
                            disablePlaceholderAnimation={false}
                            setValidationError={() => clearErrors("whatsapp")}
                            setInputValue={(value) =>
                              handleChangeHouseAdminDetails("whatsapp", value)
                            }
                          /> */}
                            </div>
                            <div className="flex flex-col gap-4">
                              <div className="flex flex-row gap-4">
                                <span className="text-[18px] font-medium">
                                  {t("user_profile.more_info")}
                                </span>
                                <span className="text-[12px] text-[#00000080] whitespace-pre-line">
                                  {t("user_profile.more_info_description")}
                                </span>
                              </div>
                              <div className="">
                                <InputDialog
                                  id="userProfile.info"
                                  validationError={errors.info?.message || ""}
                                  placeholder={t("house_manager.comment")}
                                  isBiggerInput={true}
                                  inputValue={watch("info")}
                                  disablePlaceholderAnimation={false}
                                  setValidationError={() => {}}
                                  setInputValue={(value) =>
                                    setValue("info", value)
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </form>
                      </div>
                    ) : (
                      <div className="flex-1 flex flex-col gap-6">
                        {!isLoading ? (
                          <div className="flex flex-col gap-4">
                            <div className="flex flex-row gap-4">
                              <div className="flex-1 flex flex-row gap-2">
                                <span
                                  className={`text-[18px] font-medium pt-[10px] leading-[40%]`}
                                >
                                  {userProfile?.first_name &&
                                  userProfile?.last_name
                                    ? `${userProfile?.first_name} ${userProfile?.last_name}`
                                    : t("no_data")}
                                </span>
                              </div>
                              <div className="flex-1 flex flex-col gap-2">
                                <>
                                  <div className="flex flex-row items-center justify-between">
                                    <div
                                      className={`flex flex-row gap-1 items-center ${
                                        userProfile?.phone_number
                                          ? "text-[#0027CB]"
                                          : "text-[#00000080]"
                                      }`}
                                    >
                                      <Call width={16} />
                                      <span className="text-[12px] font-medium ">
                                        {userProfile?.phone_number
                                          ? userProfile?.phone_number
                                          : t("no_data")}
                                      </span>
                                    </div>
                                  </div>
                                  <div
                                    className={`flex flex-row gap-1 items-center ${
                                      userProfile?.email
                                        ? "text-[#0027CB]"
                                        : "text-[#00000080]"
                                    }`}
                                  >
                                    <Mail width={16} />
                                    <span className="text-[12px] font-medium">
                                      {userProfile?.email
                                        ? userProfile?.email
                                        : t("no_data")}
                                    </span>
                                  </div>
                                </>
                              </div>
                            </div>
                            <div className="flex flex-col gap-[13px]">
                              <div className="flex flex-row gap-4 text-[18px] font-medium items-center">
                                <div className="flex-1">Telegram</div>
                                <div className="flex-1 flex flex-row gap-1 items-center">
                                  <TelegramIcon width={16} height={16} />
                                  <span className="text-[12px] text-[#0027CB]">
                                    {userProfile?.telegram
                                      ? parseTelegramLink(userProfile?.telegram)
                                      : t("no_data")}
                                  </span>{" "}
                                </div>
                              </div>
                              <div className="flex flex-row gap-4 text-[18px] font-medium items-center">
                                <div className="flex-1">WhatsApp</div>
                                <div className="flex-1 flex flex-row gap-1 items-center">
                                  <WhatsAppIcon width={16} height={16} />
                                  <span className="text-[12px] text-[#0027CB] ">
                                    {userProfile?.whatsapp
                                      ? userProfile?.whatsapp
                                      : t("no_data")}
                                  </span>{" "}
                                </div>
                              </div>
                              <div className="flex flex-col gap-3 text-[18px] font-medium ">
                                <span>{t("house_manager.comment")}</span>
                                <span className="text-[16px] text-[#00000080]">
                                  {userProfile?.info
                                    ? userProfile?.info
                                    : t("no_data")}
                                </span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div>loading...</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-row items-end gap-4">
                    <span className="text-[24px] font-medium leading-[100%]">
                      {t("footer.contacts")}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {/* <UnbindComponent
                      type="Whatsapp"
                      data={userProfile?.whatsapp}
                    /> */}
                    <UnbindComponent
                      type="Telegram"
                      data={parseTelegramLink(userProfile?.telegram ?? "")}
                    />
                    <UnbindComponent
                      type="SMS"
                      data={userProfile?.phone_number}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-6">
                <div className="flex flex-row items-end gap-4">
                  <span className="text-[24px] font-medium leading-[100%]">
                    {t("user_profile.notification_settings")}
                  </span>
                  <span className="text-[12px] text-[#00000080] whitespace-pre-line leading-[100%]">
                    {t("user_profile.notification_settings_description")}
                  </span>
                </div>
                <div className="flex flex-row gap-10">
                  <div className="flex flex-col gap-5">
                    <div className="flex flex-row gap-10">
                      <div className="flex flex-col gap-4 font-medium">
                        <span className="text-[18px]">
                          {t("user_profile.notify_me_of_the_following")}
                        </span>
                        <div className="flex flex-row gap-6 max-w-[516px]">
                          <div className="flex flex-col gap-4 pr-6">
                            <div className="flex flex-row gap-4">
                              <Switch
                                checked={isEnabled("invoice_created")}
                                onCheckedChange={(val) => {
                                  onOffNotification("invoice_to_debt", val);
                                  onOffNotification("invoice_created", val);
                                  switchingNotificatisPreferencesEvent({
                                    eventName: [
                                      "invoice_to_debt",
                                      "invoice_created",
                                    ],
                                  });
                                }}
                              />
                              <div className="flex flex-col gap-1">
                                <span className="text-[18px] leading-[100%]">
                                  {t("user_profile.system_notifices")}
                                </span>
                                <span className="text-[12px] text-[#00000080] leading-[120%]">
                                  {t(
                                    "user_profile.system_notifices_description"
                                  )}
                                </span>
                              </div>
                            </div>
                            {/* <div className="flex flex-row gap-4">
                          <Switch
                            checked={isEnabled(
                              "suspension_of_utility_services"
                            )}
                            onCheckedChange={(val) =>
                              onOffNotification(
                                "suspension_of_utility_services",
                                val
                              )
                            }
                          />
                          <div className="flex flex-col gap-1">
                            <span className="text-[18px] leading-[100%]">
                              {t("user_profile.suspension_of_utility_services")}
                            </span>
                            <span className="text-[12px] text-[#00000080]">
                              {t(
                                "user_profile.suspension_of_utility_services_description"
                              )}
                            </span>
                          </div>
                        </div> */}
                            <div className="flex flex-row gap-4">
                              <Switch
                                checked={isEnabled("house_notice_created")}
                                onCheckedChange={(val) => {
                                  onOffNotification(
                                    "house_notice_created",
                                    val
                                  );
                                  switchingNotificatisPreferencesEvent({
                                    eventName: ["house_notice_created"],
                                  });
                                }}
                              />
                              <div className="flex flex-col gap-1">
                                <span className="text-[18px] leading-[100%]">
                                  {t("user_profile.news")}
                                </span>
                                <span className="text-[12px] text-[#00000080]">
                                  {t("user_profile.news_description")}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-5">
                    <div className="flex flex-row gap-10">
                      <div className="flex flex-col gap-4 font-medium">
                        <span className="text-[18px]">
                          {t("user_profile.send_me_notifications_to")}
                        </span>
                        <div className="flex flex-row gap-6">
                          <div className="flex flex-col gap-4 pr-6">
                            <div className="flex flex-row gap-4">
                              <Switch
                                checked={isEnabled("enable_sms")}
                                onCheckedChange={(val) => {
                                  switchingNotificatisPreferencesChannel({
                                    channelName: "enable_sms",
                                    val: val,
                                  });
                                }}
                              />
                              <div className="flex flex-col gap-1">
                                <span className="text-[18px] leading-[100%]">
                                  SMS
                                </span>
                                <span
                                  className={`text-[12px] text-[#00000080] ${
                                    unboundErrors["enable_sms"] &&
                                    "text-[#FF0000]"
                                  }`}
                                >
                                  {unboundErrors["enable_sms"] ??
                                    userProfile?.phone_number}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-row gap-4">
                              <Switch
                                checked={isEnabled("enable_email")}
                                onCheckedChange={(val) => {
                                  switchingNotificatisPreferencesChannel({
                                    channelName: "enable_email",
                                    val: val,
                                  });
                                }}
                              />
                              <div className="flex flex-col gap-1">
                                <span className="text-[18px] leading-[100%]">
                                  E-mail
                                </span>
                                <span
                                  className={`text-[12px] text-[#00000080] ${
                                    unboundErrors["enable_email"] &&
                                    "text-[#FF0000]"
                                  }`}
                                >
                                  {unboundErrors["enable_email"] ??
                                    userProfile?.email}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col gap-4">
                            {/* <div className="flex flex-row gap-4">
                              <Switch
                                checked={isEnabled("enable_whatsapp")}
                                onCheckedChange={(val) => {
                                  switchingNotificatisPreferencesChannel({
                                    channelName: "enable_whatsapp",
                                    val: val,
                                  });
                                }}
                              />
                              <div className="flex flex-col gap-1">
                                <span className="text-[18px] leading-[100%]">
                                  WhatsApp
                                </span>
                                <span
                                  className={`text-[12px] text-[#00000080] ${
                                    unboundErrors["enable_whatsapp"] &&
                                    "text-[#FF0000]"
                                  }`}
                                >
                                  {unboundErrors["enable_whatsapp"] ??
                                    userProfile?.whatsapp}
                                </span>
                              </div>
                            </div> */}

                            <div className="flex flex-row gap-4">
                              <Switch
                                checked={isEnabled("enable_telegram")}
                                onCheckedChange={(val) => {
                                  switchingNotificatisPreferencesChannel({
                                    channelName: "enable_telegram",
                                    val: val,
                                  });
                                }}
                              />

                              <div className="flex flex-col gap-1">
                                <span className="text-[18px] leading-[100%]">
                                  Telegram
                                </span>
                                <span
                                  className={`text-[12px] text-[#00000080] ${
                                    unboundErrors["enable_telegram"] &&
                                    "text-[#FF0000]"
                                  }`}
                                >
                                  {unboundErrors["enable_telegram"] ??
                                    parseTelegramLink(
                                      userProfile?.telegram ?? ""
                                    )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {tenantRelationships?.relationships &&
                tenantRelationships?.relationships.length > 0 && (
                  <div className="flex flex-col gap-5">
                    <div className="flex flex-col gap-2 font-medium">
                      <span className="text-[24px]">
                        {t("user_profile.rentals")}
                      </span>
                      <div className="grid grid-cols-2 gap-[26px] text-[12px] text-[#00000080] leading-[100%]">
                        <span>{t("user_profile.description_rentals")}</span>
                        <span>{t("user_profile.description_rentals2")}</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-row gap-4 text-[12px] px-4 justify-between text-[#00000080]">
                        <span className="w-[20.29%]">
                          {t("house_manager.address")} / {t("e-mail")}
                        </span>
                        <span className="w-[11.93%]">
                          {t("house_manager.apartment")}
                        </span>
                        <span className="w-[14.32%]">
                          {t("user_profile.access_start_date")}
                        </span>
                        <span className="w-[14.32%]">
                          {t("user_profile.access_end_date")}
                        </span>
                        <span className="w-[7.64%]">
                          {t("house_manager.status")}
                        </span>
                        <span className="w-[110px]">
                          {t("house_manager.actions")}
                        </span>
                      </div>
                      <div className="flex flex-col gap-2">
                        {tenantRelationships?.relationships.map(
                          (tenantRelationship) => (
                            <div
                              key={tenantRelationship.apartment_id}
                              className="flex flex-col gap-1 border-[#0000001A] border-[1px] rounded-[16px] px-4"
                            >
                              <div className="flex flex-row gap-4 justify-between py-[10px]">
                                <span className="w-[20.29%]">
                                  {tenantRelationship.address}
                                </span>
                                <span className="w-[11.93%]">
                                  {tenantRelationship.apartment_number ??
                                    t("no_data")}
                                </span>
                                <span className="w-[14.32%]"></span>
                                <span className="w-[14.32%]"></span>
                                <span className="w-[7.64%]"></span>
                                <span className="">
                                  <PopUpAddRenter
                                    fetchTenantRelationships={
                                      fetchTenantRelationships
                                    }
                                    apartment_id={
                                      tenantRelationship.apartment_id
                                    }
                                    trigger={
                                      <div className="flex flex-row gap-2 cursor-pointer text-[#0027CB]">
                                        <Plus width={24} height={24} />
                                        <span className="text-[17px]">
                                          {t("user_profile.add_renter")}
                                        </span>
                                      </div>
                                    }
                                  />
                                </span>
                              </div>
                              {tenantRelationship.users.map((renter) => (
                                <div key={renter.relationship_id}>
                                  <div className="w-full h-[1px] bg-[#0000001A]" />
                                  <div className="flex flex-row gap-4 justify-between py-[10px] font-medium">
                                    <span className="w-[20.29%] flex flex-row gap-2 text-[#0027CB]">
                                      <Mail width={24} height={24} />
                                      {renter.tenant_email}
                                    </span>
                                    <span className="w-[11.93%]"></span>
                                    <span className="w-[14.32%]">
                                      {renter.start_date?.replace(/-/g, ".") ??
                                        "-"}
                                    </span>
                                    <span className="w-[14.32%]">
                                      {renter.end_date?.replace(/-/g, ".") ??
                                        "-"}
                                    </span>
                                    <span className="w-[7.64%]">
                                      {/* {renter.role && <Chip text={renter.role} status={ChipStatusEnum.BLUE} />} */}
                                    </span>
                                    <span className="w-[110px] flex justify-end text-[#00000080]">
                                      <PopUpEditRenter
                                        fetchTenantRelationships={
                                          fetchTenantRelationships
                                        }
                                        relationship_id={renter.relationship_id.toString()}
                                        trigger={
                                          <Edit width={24} height={24} />
                                        }
                                      />
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                )}
            </div>
            <div className="flex flex-col gap-4">
              <div
                onClick={
                  isEditingUserProfile
                    ? handleEdit
                    : () => setIsEditingUserProfile(true)
                }
                className="flex flex-row gap-2 bg-[#0064FF] px-[10px] py-[5px] rounded-[8px] items-center cursor-pointer"
              >
                <Edit className="text-white" width={24} height={24} />
                <span className="text-[20px] font-medium text-white">
                  {isEditingUserProfile
                    ? t("house_manager.finish_editing")
                    : t("house_manager.edit_data")}
                </span>
              </div>
              <div className="flex text-[#0027CB] text-[17px] font-medium flex-col gap-[10px]">
                <span>{t("user_profile.change_password")}</span>
                <span
                  className="flex flex-row gap-1 cursor-pointer"
                  onClick={() => handleValueChange("logout")}
                >
                  <LogOut width={24} height={24} />
                  {t("user_profile.log_out")}
                </span>
                <PopUpDeleteAccount
                  trigger={
                    <span className="flex flex-row gap-1 text-[#00000080] cursor-pointer whitespace-nowrap">
                      <TrashBind width={24} height={24} />
                      {t("user_profile.delete_this_account")}
                    </span>
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserProfile;
